import { useEffect } from "react";
import { isDesktopLoginState } from "../../../core/utils/desktopCallback";
import {
  bounceDesktopOauthCallbackIfNeeded,
  completeProviderOAuth,
  getBusinessOAuthProvider,
  publishBusinessOAuthPopupResult,
  readReconnectContext,
  resolveBusinessOauthState,
} from "./businessDigestSetup";

const PERFORMER_OAUTH_BY_STATE = {
  performer_gmail: { provider: "gmail", purpose: "performer" },
  performer_drive: { provider: "gmail", purpose: "drive" },
  performer_calendar: { provider: "google_calendar", purpose: undefined },
  performer_slack: { provider: "slack", purpose: undefined },
};

/**
 * Chrome tab helper: if this page is the Electron connector-OAuth callback,
 * move to /desktop-callback (which opens the app via taskmantis://) immediately.
 *
 * Rendered at the Router root so it runs even on SignIn (Chrome often has no
 * Electron session) and before any wizard can consume the code.
 *
 * @example
 * <Router>
 *   <DesktopOAuthLoopbackBounce />
 *   <AppRoutes />
 * </Router>
 */
export const DesktopOAuthLoopbackBounce = () => {
  bounceDesktopOauthCallbackIfNeeded();
  return null;
};

/**
 * Resolve which integration a desktop loopback `state` belongs to.
 *
 * @example
 * resolveDesktopLoopbackProvider("business_gong_popup_desktop");
 * // { provider: "gong", purpose: undefined }
 *
 * @example
 * resolveDesktopLoopbackProvider("performer_gmail_desktop");
 * // { provider: "gmail", purpose: "performer" }
 */
export const resolveDesktopLoopbackProvider = (state) => {
  const baseState = resolveBusinessOauthState(state);
  if (PERFORMER_OAUTH_BY_STATE[baseState]) {
    return PERFORMER_OAUTH_BY_STATE[baseState];
  }
  const provider = getBusinessOAuthProvider(baseState);
  if (!provider) return null;
  return {
    provider,
    purpose: provider === "gmail" ? "workflow" : undefined,
  };
};

/**
 * Exchange a connector OAuth code that Chrome handed to Electron's loopback,
 * then publish the result in this window so the waiting wizard can finish.
 *
 * Does not navigate. The Connect Gong spinner listens for the published result
 * (a URL change would not retrigger its mount-only OAuth effect).
 *
 * @example
 * await applyDesktopLoopbackPayload({
 *   code: "abc",
 *   state: "business_gong_popup_desktop",
 * });
 * // publishes { provider: "gong", credentialId: "cred_1" }
 */
export const applyDesktopLoopbackPayload = async (payload) => {
  const rawState = String(payload?.state || "").trim();
  if (isDesktopLoginState(rawState)) {
    return null;
  }
  const resolved = resolveDesktopLoopbackProvider(rawState);
  if (!resolved) {
    return null;
  }
  const { provider, purpose } = resolved;
  const oauthError = String(payload?.error || "").trim();
  const code = String(payload?.code || "").trim();

  if (oauthError && !code) {
    const result = { provider, error: oauthError };
    publishBusinessOAuthPopupResult(result);
    return result;
  }
  if (!code) {
    return null;
  }

  const existingCredentialId = String(
    readReconnectContext()?.credentialId || ""
  ).trim();
  try {
    const exchanged = await completeProviderOAuth(provider, code, {
      purpose,
      state: rawState,
      existingCredentialId: existingCredentialId || undefined,
    });
    if (provider === "google_meet" && exchanged.meetApiReady === false) {
      const result = {
        provider,
        error: exchanged.message || "Google Meet API isn't available for this account.",
      };
      publishBusinessOAuthPopupResult(result);
      return result;
    }
    const result = { provider, credentialId: exchanged.credentialId };
    if (exchanged.accountEmail) {
      result.accountEmail = exchanged.accountEmail;
    }
    publishBusinessOAuthPopupResult(result);
    return result;
  } catch (error) {
    const result = {
      provider,
      error: error?.message || "OAuth callback failed",
    };
    publishBusinessOAuthPopupResult(result);
    return result;
  }
};

/**
 * Electron: receive connector OAuth codes from the loopback server, exchange
 * them with this window's session, and notify the waiting connector UI.
 *
 * @example
 * useDesktopOAuthLoopback();
 * // Gong callback in Chrome → 127.0.0.1:51739 → wizard leaves "Waiting…"
 */
export const useDesktopOAuthLoopback = () => {
  useEffect(() => {
    if (!window.starmeshDesktop?.isElectron || !window.starmeshDesktop.onOAuthCallback) {
      return undefined;
    }

    const consumePayload = (payload) => {
      void applyDesktopLoopbackPayload(payload);
    };

    const pendingReader = window.starmeshDesktop.consumePendingOAuthCallbacks;
    if (typeof pendingReader === "function") {
      void pendingReader()
        .then((queued) => {
          if (!Array.isArray(queued)) return;
          queued.forEach(consumePayload);
        })
        .catch(() => {
          // Best-effort fallback: real-time callback subscription still runs below.
        });
    }

    return window.starmeshDesktop.onOAuthCallback(consumePayload);
  }, []);
};
